import { useNavigate } from "react-router-dom";
import { IconBuilding, IconUser } from "@tabler/icons-react";
import Text from "../UI/Text";
import Avatar from "../UI/Avatar";
import AvatarStack from "../UI/AvatarStack";
import styles from "./ProfileListCard.module.css";
// @ts-expect-error pure js
import { StatusFiscal } from "../../models/profile";
import { Button } from "@base-ui/react/button";
// @ts-expect-error pure js
import useAccount from "../../hooks/use-account";
import Card from "../UI/Card.tsx";
import Status from "../UI/Status.tsx";
import Tooltip from "../UI/Tooltip";
import type { ProfileBailleur } from "./types";

const StatusFiscalShort: Record<string, string> = {
  [StatusFiscal.NUMICRO]: "Micro foncier",
  [StatusFiscal.NUREEL]: "Réel foncier",
  [StatusFiscal.LMNPMICRO]: "LMNP micro-BIC",
  [StatusFiscal.LMNPREEL]: "LMNP réel",
  [StatusFiscal.IS]: "Société à l'IS",
};

function getStatusLabel(profileBailleur: ProfileBailleur): string | null {
  if (profileBailleur.regime_fiscal) {
    return StatusFiscalShort[profileBailleur.regime_fiscal] || null;
  }
  if (profileBailleur.is_societe) {
    return "Société";
  }
  return null;
}

function getInitials(email: string): string {
  return email.substring(0, 2).toUpperCase();
}

interface ProfileListCardProps {
  profileBailleur: ProfileBailleur;
  currentUserEmail?: string;
}

export default function ProfileListCard({
  profileBailleur,
}: ProfileListCardProps) {
  const navigate = useNavigate();
  const isSociete = profileBailleur.is_societe;
  const statusLabel = getStatusLabel(profileBailleur);
  const allComptes = profileBailleur.comptes || [];
  const { account } = useAccount();
  const sharedWith = allComptes.filter(
    (compte) => compte.mail !== account?.mail,
  );

  return (
    <Card
      padding={"sm"}
      as={Button}
      onClick={() => navigate(`${profileBailleur.id}`)}
      className={styles.card}
    >
      <div className={styles.row}>
        <Avatar
          icon={isSociete ? <IconBuilding /> : <IconUser />}
          size="lg"
          variant="yellow"
        />
        <Text weight="bold" as="span">
          {profileBailleur.nom_profile}
        </Text>
        {statusLabel && (
          <Status color="info" text={statusLabel} className={styles.badge} />
        )}
      </div>
      <div className={styles.sharedSection}>
        <Text
          variant="deci"
          weight="medium"
          as="span"
          className={styles.sharedLabel}
        >
          Partagé avec
        </Text>
        {sharedWith.length > 0 ? (
          <Tooltip
            content={
              <>
                {sharedWith.map((compte) => (
                  <div key={compte.mail}>
                    {compte.mail} ({compte.droits})
                  </div>
                ))}
              </>
            }
          >
            <div className={styles.sharedSummary}>
              <AvatarStack
                items={sharedWith}
                getInitials={(compte) => getInitials(compte.mail)}
                max={1}
                size="sm"
                variant="blue"
              />
              <Text variant="deci" as="span">
                {sharedWith.length === 1
                  ? sharedWith[0].mail
                  : `${sharedWith.length} personnes`}
              </Text>
            </div>
          </Tooltip>
        ) : (
          <Text variant="deci" as="span">
            personne
          </Text>
        )}
      </div>
    </Card>
  );
}
